import { type CSSProperties, useEffect, useRef } from 'react';
import videojs, { type VideoJsPlayer, type VideoJsPlayerOptions } from 'video.js';

import 'video.js/dist/video-js.css';
import { LiveWatchdog } from './LiveWatchdog.js';
import styles from './VideoJs.module.scss';
import { StreamsApi } from '../api/index.js';

interface VideoJsProps {
  options: VideoJsPlayerOptions;
  token: string;
  onReady?: (player: VideoJsPlayer, streamId: string) => void;
  onError?: (error: MediaError | null, streamId: string) => void;
  onStreamStop?: (streamId: string) => void;
  streamId: string;
  live: boolean;
  style?: CSSProperties;
  src?: string;
}

const tokenByStreamId: { [p: string]: string } = {};

videojs.Vhs.xhr.beforeRequest = function (options) {
  const streamId = (options.uri?.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-low|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-high/gm,
  ) || [])[0];
  const token = options.uri?.split('=')[1];
  if (streamId && token) {
    tokenByStreamId[streamId] = token;
  }
  if (!options.uri?.includes('?')) {
    // @ts-expect-error streamId narrowing isn't carried through from the outer guard
    options.uri = options.uri + '?token=' + tokenByStreamId[streamId];
  }
  return options;
};

const VideoJS = (props: VideoJsProps) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const watchdogRef = useRef<LiveWatchdog | null>(null);
  const { live, options, onReady, onStreamStop, token, streamId, style, onError, src } = props;

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement('video-js');

      videoElement.classList.add('vjs-big-play-centered');
      videoElement.classList.add('video-js');
      videoElement.classList.add('vjs-default-skin');
      videoRef.current?.appendChild(videoElement);
      const player = (playerRef.current = videojs(videoElement, {
        html5: {
          vhs: {
            overrideNative: true,
            useDtsForTimestampOffset: true,
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
        ...options,
      }));
      player.on('canplaythrough', () => {
        onReady && onReady(player, streamId);
      });

      player.on('error', () => {
        onError && onError(player.error(), streamId);
      });
      player.on('ended', () => {
        onStreamStop && onStreamStop(streamId);
      });
      if (live) {
        player.src({
          src: src ?? `${StreamsApi.MEDIA_URL}/streams/${encodeURIComponent(`${streamId}.m3u8`)}?token=${token}`,
          type: 'application/x-mpegURL',
        });
        watchdogRef.current = new LiveWatchdog(player, () => onError && onError(null, streamId));
        watchdogRef.current.start();
      } else {
        player.src({
          src: src ?? `${StreamsApi.MEDIA_URL}/streams/${encodeURIComponent(`${streamId}.mp4`)}`,
          type: 'video/mp4',
        });
      }

      player.fill(true);

      // You could update an existing player in the `else` block here
      // on prop change, for example:
    } else {
      const player = playerRef.current;
      // (player.tech() as any).vhs.xhr.beforeRequest = function (options: any) {
      //   if (!options.uri?.includes('token')) {
      //     options.uri = options.uri + '?token=' + token;
      //   }
      //   return options;
      // };
      player.autoplay(options.autoplay || false);
      if (options.sources) {
        player.src(options.sources);
      }
    }
  }, [options, videoRef]);

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      watchdogRef.current?.stop();
      watchdogRef.current = null;
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <div
      data-vjs-player
      data-test-id={streamId}
      style={style}
      className={styles['videojs__container']}
      ref={videoRef}
    ></div>
  );
};

export default VideoJS;
